
import React, { useState, useRef } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Save } from "lucide-react";
import { uploadStoryImage } from "./utils/imageHandlers";

// Import the refactored components
import StoryEditorEnglishContent from "./StoryEditorEnglishContent";
import StoryEditorArabicContent from "./StoryEditorArabicContent";
import StoryEditorMetadata from "./StoryEditorMetadata";
import StoryEditorSDGs from "./StoryEditorSDGs";

interface StoryEditorProps {
  story: any;
  onSave: (story: any) => void;
  onCancel: () => void;
}

const StoryEditor: React.FC<StoryEditorProps> = ({ 
  story, 
  onSave, 
  onCancel 
}) => {
  const { language } = useLanguage();
  const [formState, setFormState] = useState({ ...story });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  
  const handleSdgToggle = (sdgId: number) => {
    if (formState.sdgIds.includes(sdgId)) {
      setFormState({
        ...formState,
        sdgIds: formState.sdgIds.filter((id: number) => id !== sdgId)
      });
    } else {
      setFormState({
        ...formState,
        sdgIds: [...formState.sdgIds, sdgId]
      });
    }
  };
  
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: language === "en" ? "Invalid file type" : "نوع الملف غير صالح",
        description: language === "en" ? "Please upload an image file" : "يرجى تحميل ملف صورة",
        variant: "destructive"
      });
      return;
    }
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: language === "en" ? "File too large" : "الملف كبير جدًا",
        description: language === "en" ? "Image must be smaller than 5MB" : "يجب أن تكون الصورة أصغر من 5 ميجابايت",
        variant: "destructive"
      });
      return;
    }
    
    setIsUploading(true);
    
    try {
      console.log("Starting image upload for file:", file.name);
      
      // Upload image using the utility function
      const publicUrl = await uploadStoryImage(file);
      console.log("Upload successful, received URL:", publicUrl);
      
      // Update form state with the new image URL
      setFormState({
        ...formState,
        image: publicUrl
      });
      
      toast({
        title: language === "en" ? "Image uploaded" : "تم تحميل الصورة",
        description: language === "en" ? "Image has been successfully uploaded" : "تم تحميل الصورة بنجاح",
      });
    } catch (error) {
      console.error("Error in handleImageUpload:", error);
      toast({
        title: language === "en" ? "Upload failed" : "فشل التحميل",
        description: language === "en" ? "Failed to upload image. Please try again." : "فشل تحميل الصورة. يرجى المحاولة مرة أخرى.",
        variant: "destructive"
      });
      
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setIsUploading(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* English Content */}
        <StoryEditorEnglishContent 
          formState={formState} 
          onChange={setFormState} 
        />
        
        {/* Arabic Content */}
        <StoryEditorArabicContent 
          formState={formState} 
          onChange={setFormState} 
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Metadata */}
        <StoryEditorMetadata 
          formState={formState} 
          onChange={setFormState}
          onImageUpload={handleImageUpload}
          isUploading={isUploading}
          fileInputRef={fileInputRef}
        />
        
        {/* Related SDGs */}
        <StoryEditorSDGs 
          formState={formState} 
          onSdgToggle={handleSdgToggle} 
        />
      </div>
      
      <div className="flex justify-end gap-4">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
        >
          {language === "en" ? "Cancel" : "إلغاء"}
        </Button>
        <Button 
          type="button"
          className="bg-palsustain-green hover:bg-palsustain-green/90"
          onClick={() => onSave(formState)}
        >
          <Save className="mr-2 h-4 w-4" />
          {language === "en" ? "Save Story" : "حفظ القصة"}
        </Button>
      </div>
    </div>
  );
};

export default StoryEditor;
